import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-d65d4c66"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["src"]
const _hoisted_2 = { class: "rounded-md bg-yellow-400/80 p-4 text-center text-xs sm:text-sm text-gray-50 flex-1 m-5" }
const _hoisted_3 = {
  key: 1,
  class: "vjs-loading-spinner"
}
const _hoisted_4 = ["title"]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "vjs-icon-placeholder",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "vjs-control-text",
  "aria-live": "polite"
}, "Play", -1))
const _hoisted_7 = [
  _hoisted_5,
  _hoisted_6
]
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "vjs-live-control vjs-control" }, [
  /*#__PURE__*/_createElementVNode("div", {
    class: "vjs-live-display",
    "aria-live": "off"
  }, [
    /*#__PURE__*/_createElementVNode("span", { class: "vjs-control-text" }, "Stream Type "),
    /*#__PURE__*/_createTextVNode("LIVE ")
  ])
], -1))
const _hoisted_9 = ["title"]
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "vjs-icon-placeholder",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "vjs-control-text",
  "aria-live": "polite"
}, "Fullscreen", -1))
const _hoisted_12 = [
  _hoisted_10,
  _hoisted_11
]

import { ArrowPathIcon } from "@heroicons/vue/24/outline";
import { useCurrentCustomerName, useCurrentSiteId } from "@oculai/planner";
import mobile from "is-mobile";
import screenfull from "screenfull";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import {
  useFullScreen4to3RatioDimensionStyle,
  useFullWindow4to3RatioDimensionStyle,
} from "@/composables/screen";
import StreamRepository from "@/repositories/StreamRepository";
import logger from "@/services/logger";


export default /*#__PURE__*/_defineComponent({
  __name: 'LiveBaseImage',
  props: {
  cameraId: {
    type: String,
    required: true,
  },
  showControls: {
    type: Boolean,
    required: false,
    default: true,
  },
  isPublic: {
    type: Boolean,
    required: false,
  },
},
  setup(__props) {

const imageUrl = ref<string | null>(null);
const loading = ref(false);
const hasError = ref(false);
const isUserActive = ref(true);
const isPlaying = ref(false);
const isFullScreen = ref(false);
const isFakeFullScreen = ref(false);
const containerRef = ref<HTMLDivElement | null>(null);
const userActiveInitialTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const refreshTimer = ref<ReturnType<typeof setInterval> | null>(null);
const abortController = ref<AbortController | null>(null);

const isMobileDevice = mobile();
const currentCustomerName = useCurrentCustomerName();
const currentSiteId = useCurrentSiteId();
const fullScreen4to3RatioDimensionStyle = useFullScreen4to3RatioDimensionStyle();
const fullWindow4to3RatioDimensionStyle = useFullWindow4to3RatioDimensionStyle();
const route = useRoute();

const props = __props;

const full4to3RatioDimensionStyle = computed(() => {
  if (isFakeFullScreen.value) {
    return fullWindow4to3RatioDimensionStyle.value;
  }
  if (isFullScreen.value) {
    return fullScreen4to3RatioDimensionStyle.value;
  }
  return "";
});

const loadLiveImage = () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  hasError.value = false;

  abortController.value = new AbortController();

  const token = route.params.token as string;

  const promise = props.isPublic
    ? StreamRepository.loadPublicLiveImage(
        currentCustomerName,
        currentSiteId,
        props.cameraId,
        token,
        abortController.value.signal,
      )
    : StreamRepository.loadLiveImage(
        currentCustomerName,
        currentSiteId,
        props.cameraId,
        abortController.value.signal,
      );

  promise
    .then((imageArrayBuffer) => {
      if (imageArrayBuffer) {
        if (imageUrl.value) {
          window.URL.revokeObjectURL(imageUrl.value);
        }
        const blob = new Blob([imageArrayBuffer], { type: "image/jpeg" });
        imageUrl.value = window.URL.createObjectURL(blob);
        startUserActiveInitialTimer();
      } else {
        hasError.value = true;
      }
    })
    .catch((error) => {
      hasError.value = true;
      logger.error(error);
    })
    .finally(() => {
      loading.value = false;
    });
};

const handleImageClick = () => {
  if (!props.showControls) {
    return;
  }
  if (isPlaying.value) {
    stopPlay();
  } else {
    startPlay({ dontReload: true });
  }
};

const handlePlayClick = () => {
  if (isPlaying.value) {
    stopPlay();
  } else {
    startPlay();
  }
};

const handleFullScreenClick = () => {
  if (!screenfull.isEnabled) {
    isFakeFullScreen.value = !isFakeFullScreen.value;
    return;
  }
  if (screenfull.isFullscreen) {
    screenfull.exit();
  } else if (containerRef.value) {
    screenfull.request(containerRef.value, { navigationUI: "hide" });
  }
};

const handleFullscreenChange = () => {
  isFullScreen.value = screenfull.isFullscreen;
};

const startUserActiveInitialTimer = () => {
  if (userActiveInitialTimer.value === null && isPlaying.value) {
    userActiveInitialTimer.value = setTimeout(() => {
      isUserActive.value = false;
    }, 3000);
  }
};

const stopUserActiveInitialTimer = () => {
  if (userActiveInitialTimer.value !== null) {
    clearTimeout(userActiveInitialTimer.value);
  }
};

const startPlay = (options?: { dontReload: boolean }) => {
  isPlaying.value = true;
  if (!options?.dontReload) {
    loadLiveImage();
  }
  refreshTimer.value = setInterval(() => {
    loadLiveImage();
  }, 60 * 1000);
};

const stopPlay = () => {
  isPlaying.value = false;
  if (refreshTimer.value !== null) {
    clearInterval(refreshTimer.value);
  }
};

onMounted(() => {
  if (screenfull.isEnabled) {
    screenfull.on("change", handleFullscreenChange);
  }
  if (isMobileDevice) {
    loadLiveImage();
  } else {
    startPlay();
  }
});

onUnmounted(() => {
  if (screenfull.isEnabled) {
    screenfull.off("change", handleFullscreenChange);
  }
  stopUserActiveInitialTimer();
  stopPlay();
  if (abortController.value && loading.value) {
    abortController.value.abort();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(isFakeFullScreen.value ? 'fixed inset-0 bg-oaiGray-900' : ''),
    onClick: _cache[6] || (_cache[6] = ($event: any) => (isFakeFullScreen.value = false)),
    style: _normalizeStyle(isFakeFullScreen.value ? 'z-index: 2147483004' : '')
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["bg-oaiGray-900 video-js vjs-live vjs-4-3", {
        'vjs-waiting': loading.value,
        'vjs-user-active': isUserActive.value,
        'vjs-user-inactive': !isUserActive.value,
        'vjs-has-started': imageUrl.value,
        'vjs-playing': isPlaying.value,
        'vjs-paused': !isPlaying.value,
        'vjs-fullscreen': isFullScreen.value || isFakeFullScreen.value,
        'vjs-controls-enabled': __props.showControls,
      }]),
      onMouseenter: _cache[3] || (_cache[3] = ($event: any) => {
        stopUserActiveInitialTimer();
        isUserActive.value = true;
      }),
      onMouseleave: _cache[4] || (_cache[4] = ($event: any) => (isUserActive.value = false)),
      onMousemove: _cache[5] || (_cache[5] = ($event: any) => (isUserActive.value = true)),
      ref_key: "containerRef",
      ref: containerRef
    }, [
      _createElementVNode("div", {
        class: "image-content select-none",
        style: _normalizeStyle(full4to3RatioDimensionStyle.value)
      }, [
        (imageUrl.value)
          ? (_openBlock(), _createElementBlock("img", {
              key: 0,
              src: imageUrl.value,
              alt: "live image",
              onClick: [
                handleImageClick,
                _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
              ],
              onDblclick: handleFullScreenClick
            }, null, 40, _hoisted_1))
          : _createCommentVNode("", true)
      ], 4),
      (hasError.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "error-content",
            style: _normalizeStyle(`${full4to3RatioDimensionStyle.value}; height: auto;`)
          }, [
            _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.$t("video_player.unable_to_play_message")), 1)
          ], 4))
        : _createCommentVNode("", true),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3))
        : _createCommentVNode("", true),
      (__props.showControls)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: "vjs-control-bar",
            onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("button", {
              class: _normalizeClass(["vjs-play-control vjs-control vjs-button", {
            'vjs-playing': isPlaying.value,
            'vjs-paused': !isPlaying.value,
          }]),
              type: "button",
              title: isPlaying.value ? 'Pause' : 'Play',
              "aria-disabled": "false",
              onClick: handlePlayClick
            }, _hoisted_7, 10, _hoisted_4),
            _hoisted_8,
            _createElementVNode("div", {
              class: "flex items-center cursor-pointer",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (loadLiveImage()))
            }, [
              _createVNode(_unref(ArrowPathIcon), { class: "w-4 h-4 text-white" })
            ]),
            _createElementVNode("button", {
              class: "vjs-fullscreen-control vjs-control vjs-button",
              type: "button",
              title: isFullScreen.value ? 'Non-Fullscreen' : 'Fullscreen',
              "aria-disabled": "false",
              onClick: handleFullScreenClick
            }, _hoisted_12, 8, _hoisted_9)
          ]))
        : _createCommentVNode("", true)
    ], 34)
  ], 6))
}
}

})